import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, MicOff, Loader2, X, Check, Sparkles, ChevronDown, ChevronUp, Send } from "lucide-react";
import SendRecapModal from "@/components/SendRecapModal";

const STYLES = [
  { value: "paragraph", label: "Paragraph", hint: "Short prose · 2-4 sentences" },
  { value: "bullets", label: "Bullets", hint: "One line per point · max 6" },
];

const CONTEXTS = [
  { value: "session", label: "Session" },
  { value: "assessment", label: "Assessment" },
  { value: "general", label: "General" },
];

/**
 * VoiceNoteRecorder — coach taps mic, records via MediaRecorder, endpoint
 * transcribes + summarises via Whisper + Claude, coach reviews the cleaned
 * summary, tap Save to append it as a normal coach note.
 *
 * Two visual variants controlled by `variant`:
 *   · "inline"  – pill button inline with other actions
 *   · "fab"     – floating action button (fixed bottom-right)
 *
 * The recording state + review modal are the same for both.
 */
export default function VoiceNoteRecorder({
  studentId,
  studentName = "",
  variant = "inline",
  defaultContext = "session",
  className = "",
  onSaved,
}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [style, setStyle] = useState("paragraph");
  const [ctx, setCtx] = useState(defaultContext);
  const [elapsed, setElapsed] = useState(0);
  const [permissionError, setPermissionError] = useState("");
  const [result, setResult] = useState(null); // {transcript, summary, style, model}
  const [editableSummary, setEditableSummary] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supported, setSupported] = useState(true);
  const [recapOpen, setRecapOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Feature-detect MediaRecorder on mount so we can gray out the button on
  // browsers that don't support it (Safari <14, some in-app browsers).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok = !!(window.MediaRecorder && navigator.mediaDevices?.getUserMedia);
    setSupported(ok);
  }, []);

  // Tick the recording timer every 200ms — 5-decimal accuracy isn't needed,
  // this just gives the coach a sense of how long they've been talking.
  useEffect(() => {
    if (!recording) { setElapsed(0); return; }
    const started = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 250);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [recording]);

  // Safety: stop the mic if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
      try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch { /* noop */ }
    };
  }, []);

  const pickMimeType = () => {
    // Chrome prefers webm/opus; Safari 14.5+ supports mp4/aac. We probe both
    // and let the browser pick — Whisper accepts both formats.
    if (!window.MediaRecorder?.isTypeSupported) return "";
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
    return candidates.find((c) => window.MediaRecorder.isTypeSupported(c)) || "";
  };

  const startRecording = async () => {
    setPermissionError("");
    setResult(null);
    if (!supported) {
      toast.error("This browser doesn't support in-app recording. Try Chrome or Safari 14+.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        // Assemble the blob then send. We do this in onstop (not the caller)
        // to guarantee the final data chunk has arrived — otherwise we can
        // end up with a truncated clip missing the last ~1s.
        try {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
          streamRef.current?.getTracks()?.forEach((t) => t.stop());
          streamRef.current = null;
          if (blob.size < 1000) {
            toast.error("Recording too short — try holding the mic for at least a second.");
            return;
          }
          await uploadAndProcess(blob, rec.mimeType || "audio/webm");
        } catch (e) {
          toast.error("Something went wrong finishing the recording. Try again.");
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      // getUserMedia rejects on permission denied, no device, or insecure origin.
      const msg = e?.name === "NotAllowedError"
        ? "Microphone access was denied. Enable it in your browser settings and try again."
        : e?.name === "NotFoundError"
        ? "No microphone found on this device."
        : `Couldn't start recording (${e?.name || "error"}).`;
      setPermissionError(msg);
      toast.error(msg);
    }
  };

  const stopRecording = () => {
    // Stopping fires `onstop` which handles the upload — keep this synchronous.
    setRecording(false);
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
  };

  const uploadAndProcess = async (blob, mime) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      // Give it a filename so the server can pick the right extension.
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("mpeg") ? "mp3" : "webm";
      fd.append("audio", new File([blob], `voice-note.${ext}`, { type: mime }));
      fd.append("style", style);
      fd.append("context", ctx);
      const { data } = await api.post(`/coach/players/${studentId}/voice-note`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000, // whisper + claude can take 20-40s on longer clips
      });
      setResult(data);
      setEditableSummary(data.summary || data.transcript || "");
      if (data.cleanup_failed) {
        toast.warning("Transcript ready — AI cleanup fell back to raw text. You can edit before saving.");
      } else {
        toast.success("Note ready — review and save.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't process the recording. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const saveNote = async () => {
    const trimmed = (editableSummary || "").trim();
    if (trimmed.length < 2) {
      toast.error("Note is empty — add a couple of words before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/coach-notes`, {
        body: trimmed,
        context: result?.context || ctx,
      });
      toast.success("Voice note saved to the timeline");
      window.dispatchEvent(new CustomEvent("coach-notes:changed", { detail: { studentId } }));
      setResult(null);
      setEditableSummary("");
      setShowTranscript(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fmtDur = (s) => {
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, "0");
    return `${m}:${ss}`;
  };

  const busy = recording || processing || saving;

  // --- Trigger button (varies by variant) ---
  const trigger = variant === "fab" ? (
    <button
      onClick={recording ? stopRecording : startRecording}
      disabled={processing || saving}
      className={`fixed bottom-6 right-6 z-40 rounded-full w-14 h-14 flex items-center justify-center shadow-2xl transition-all disabled:opacity-50 ${
        recording ? "bg-[#FF4500] animate-pulse" : "bg-[#CCFF00] hover:scale-105"
      }`}
      data-testid="voice-note-fab"
      title={recording ? "Stop recording" : "Voice note"}
    >
      {processing ? <Loader2 className="w-6 h-6 text-[#0A0A0A] animate-spin" /> : (
        recording ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-[#0A0A0A]" />
      )}
    </button>
  ) : (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="voice-note-inline">
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={processing || saving}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors disabled:opacity-50 ${
          recording
            ? "border-[#FF4500] bg-[#FF4500]/10 text-[#FF4500] animate-pulse"
            : "border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00]"
        }`}
        data-testid="voice-note-toggle"
      >
        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : recording ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
        {recording ? `Recording ${fmtDur(elapsed)} · tap to stop` : processing ? "Processing…" : "Voice note"}
      </button>
      {!recording && !processing && !result && (
        <div className="inline-flex items-center gap-1 border border-[#262626] bg-[#0A0A0A]">
          {STYLES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStyle(s.value)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1.5 transition-colors ${
                style === s.value ? "text-[#CCFF00]" : "text-[#525252] hover:text-[#A3A3A3]"
              }`}
              title={s.hint}
              data-testid={`voice-note-style-${s.value}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // --- Review modal (portaled so ancestor .fade-up transforms don't scope it) ---
  const reviewModal = result && createPortal((
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="voice-note-review-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#CCFF00]" />
            <div>
              <div className="text-sm font-medium">Review voice note</div>
              {studentName && <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">for {studentName}</div>}
            </div>
          </div>
          <button
            onClick={() => { setResult(null); setEditableSummary(""); setShowTranscript(false); }}
            className="p-1 hover:bg-[#141414]"
            data-testid="voice-note-review-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-mono text-[10px]">Cleaned-up note ({result.style})</div>
              <div className="inline-flex items-center gap-1 border border-[#262626]">
                {CONTEXTS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setCtx(c.value)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 transition-colors ${
                      ctx === c.value ? "text-[#CCFF00]" : "text-[#525252] hover:text-[#A3A3A3]"
                    }`}
                    data-testid={`voice-note-context-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={editableSummary}
              onChange={(e) => setEditableSummary(e.target.value)}
              rows={result.style === "bullets" ? 8 : 5}
              maxLength={2000}
              className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none leading-relaxed"
              data-testid="voice-note-summary-edit"
            />
            <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1">
              {editableSummary.length} / 2000 · {result.model}
              {result.cleanup_failed && " · cleanup fell back to raw transcript"}
            </div>
          </div>

          <button
            onClick={() => setShowTranscript((v) => !v)}
            className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1"
            data-testid="voice-note-toggle-transcript"
          >
            {showTranscript ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showTranscript ? "Hide" : "Show"} original transcript
          </button>
          {showTranscript && (
            <div className="text-xs text-[#A3A3A3] italic border-l-2 border-[#262626] pl-3 leading-relaxed" data-testid="voice-note-transcript">
              "{result.transcript}"
            </div>
          )}
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-between">
          <div className="text-[10px] text-[#A3A3A3]">
            Only the cleaned-up note above is saved to the timeline
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setRecapOpen(true)}
              disabled={saving || (editableSummary || "").trim().length < 10}
              className="text-xs px-3 py-2 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00]/10 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="voice-note-send-recap"
              title="Send this as a friendly recap to the player"
            >
              <Send className="w-3 h-3" /> Send to player
            </button>
            <button
              onClick={() => { setResult(null); setEditableSummary(""); setShowTranscript(false); }}
              className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]"
              data-testid="voice-note-review-cancel"
            >
              Discard
            </button>
            <button
              onClick={saveNote}
              disabled={saving || editableSummary.trim().length < 2}
              className="text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="voice-note-save"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {saving ? "Saving…" : "Save to timeline"}
            </button>
          </div>
        </div>
      </div>
      <SendRecapModal
        open={recapOpen}
        onClose={() => setRecapOpen(false)}
        studentId={studentId}
        studentName={studentName}
        sourceBody={editableSummary}
      />
    </div>
  ), document.body);

  return (
    <>
      {trigger}
      {permissionError && variant === "inline" && (
        <div className="text-[10px] text-[#FF4500] mt-1" data-testid="voice-note-error">{permissionError}</div>
      )}
      {reviewModal}
    </>
  );
}
